import { useRef } from 'react';
import { Product, ProductDetailsTabsIds } from '@setvi/shared/interfaces';
import { SText, SVirtualTable, TableLoader } from '@setvi/shared/components';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Customer } from 'Services/MongoRealm/Types';
import { ActionBar } from 'componentsV2/product/action-bar';
import { disableSelectProductTooltip } from 'pages/products/components/products-list';
import { Box } from '@material-ui/core';
import { useSimilarProducts, useSimilarProductsColumns } from './hooks';
import { rowKeyGetter } from '../associated-products';
import { ContentTitleContainer } from '../product-info';
import { ProductsEmptyState } from '../../components';

export interface SimilarProductsProps {
  product: Product;
  selectedProducts: Product[];
  scrollParentRef: any;
  selectedCustomer: Customer;
  readOnly: boolean;
  canSelectProducts: boolean;
  closeDialog?: () => void;
  handleProductSelect: (isChecked: boolean, product: Product[]) => void;
  openProductDetails?: (
    product: Product,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  handleEmail: () => void;
  handleSelected: () => void;
  handleCloseActionBar: () => void;
}

export const SimilarProducts = ({
  product,
  readOnly = false,
  scrollParentRef,
  selectedCustomer,
  selectedProducts,
  canSelectProducts,
  closeDialog,
  handleEmail,
  handleSelected,
  openProductDetails,
  handleProductSelect,
  handleCloseActionBar
}: SimilarProductsProps) => {
  const { products, isLoading } = useSimilarProducts({
    product,
    selectedCustomer
  });
  const { columns } = useSimilarProductsColumns({
    selectedCustomer,
    openProductDetails
  });
  const tableRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: products?.length || 0,
    estimateSize: () => 85,
    getScrollElement: () => scrollParentRef.current,
    overscan: 5,
    scrollMargin: tableRef.current?.offsetTop ?? 0
  });

  const openModal = (prod: Product) => {
    closeDialog?.();
    openProductDetails(prod);
  };

  if (!products?.length && !isLoading)
    return (
      <Box display="flex" flexDirection="column" flex={1}>
        <ContentTitleContainer>
          <SText title="Similar Products" weight="semibold" />
        </ContentTitleContainer>
        <ProductsEmptyState title="No similar products found" />
      </Box>
    );

  return (
    <>
      <div ref={tableRef} style={{ width: '100%' }}>
        <ContentTitleContainer>
          <SText title="Similar Products" weight="semibold" />
        </ContentTitleContainer>
        <SVirtualTable
          newDesign
          stripped
          list={products}
          options={{ scrollMargin: virtualizer.options.scrollMargin }}
          vList={virtualizer?.getVirtualItems()}
          height={virtualizer?.getTotalSize()}
          columns={columns}
          row={{
            onClick: openModal,
            ...(!readOnly && {
              checkbox: (prod: Product) => {
                const checked = selectedProducts?.some(
                  i => rowKeyGetter(i) === rowKeyGetter(prod)
                );
                return {
                  checked,
                  disabled: !canSelectProducts && !checked,
                  tooltip:
                    !canSelectProducts &&
                    !checked &&
                    disableSelectProductTooltip,
                  onChange: e => handleProductSelect(e.target.checked, [prod])
                };
              }
            }),
            rowStyleGetter: () => ({ maxHeight: 'fit-content' })
          }}
          keyGetter={i => i.sku}
        />
        {isLoading && <TableLoader columns={columns} />}
      </div>

      {handleEmail &&
        selectedProducts?.length > 0 &&
        !readOnly &&
        !isLoading && (
          <ActionBar
            selectedProducts={selectedProducts}
            buttonLabel="Email"
            handleEmail={handleEmail}
            handleSelected={handleSelected}
            handleClose={handleCloseActionBar}
          />
        )}
    </>
  );
};
